import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "../components/layout/PageContainer/PageContainer";
import { Button } from "../components/ui/Button/Button";
import { SearchBar } from "../components/composite/SearchBar/SearchBar";
import { EmptyState } from "../components/composite/EmptyState/EmptyState";
import { LoadingState } from "../components/composite/LoadingState/LoadingState";
import {
  CustomerCard,
  CustomerCreateDialog,
} from "../components/features/customer";
import { useCustomer } from "../contexts/CustomerContext";
import { useAuth } from "../contexts/AuthContext";
import { ROUTES } from "../constants/routes";
import { cn } from "../lib/cn";

/**
 * CustomerSelection Page
 * Allows consultants and admins to select which customer to work with
 * Simple users are auto-redirected to dashboard with their customer
 */
export default function CustomerSelection() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    accessibleCustomers,
    selectedCustomer,
    selectCustomer,
    addAccessibleCustomer,
    loadAccessibleCustomers,
    loading,
    error,
  } = useCustomer();

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Check if user can create customers
  const isRegularUser = user?.role === "user";
  const canCreateCustomer = isRegularUser
    ? accessibleCustomers?.length === 0 // Regular users: only if no customers
    : true; // Admin/Consultant: always

  // Filter customers based on search query
  useEffect(() => {
    if (!accessibleCustomers) {
      setFilteredCustomers([]);
      return;
    }

    if (!searchQuery.trim()) {
      setFilteredCustomers(accessibleCustomers);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = accessibleCustomers.filter((customer) => {
      return (
        customer.name?.toLowerCase().includes(query) ||
        customer.description?.toLowerCase().includes(query) ||
        customer.contactPerson?.toLowerCase().includes(query) ||
        customer.contactEmail?.toLowerCase().includes(query)
      );
    });

    setFilteredCustomers(filtered);
  }, [accessibleCustomers, searchQuery]);

  /**
   * Handle customer selection
   */
  const handleSelectCustomer = (customer) => {
    selectCustomer(customer);
    navigate(ROUTES.DASHBOARD);
  };

  /**
   * Handle search clear
   */
  const handleClearSearch = () => {
    setSearchQuery("");
  };

  /**
   * Handle customer created successfully
   */
  const handleCustomerCreated = (newCustomer) => {
    // Add the new customer to the accessible customers list (instant UI update)
    addAccessibleCustomer(newCustomer);

    // For regular users: auto-select the newly created customer and navigate
    if (isRegularUser) {
      selectCustomer(newCustomer);
      navigate(ROUTES.DASHBOARD);
    }
    // For consultants/admins: customer will appear in the grid immediately
  };

  return (
    <PageContainer
      title="Select Customer"
      description={`Choose which customer to work with • ${
        accessibleCustomers?.length || 0
      } customers available`}
      actions={
        canCreateCustomer &&
        !loading && (
          <Button
            design="Emphasized"
            icon="add"
            onClick={() => setCreateDialogOpen(true)}
          >
            New Customer
          </Button>
        )
      }
    >
      <div className="space-y-6">
        {/* Search Bar */}
        <SearchBar
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search customers by name, description, or contact..."
          onClear={handleClearSearch}
        />

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <LoadingState text="Loading customers..." variant="spinner" />
        )}

        {/* Empty State - No customers assigned */}
        {!loading && accessibleCustomers?.length === 0 && (
          <EmptyState
            title={
              isRegularUser
                ? "Create Your Customer Profile"
                : "No customers assigned"
            }
            description={
              isRegularUser
                ? "Get started by creating your customer profile to track services and analyze changes."
                : user?.role === "consultant"
                ? "You have not been assigned to any customers yet. Please contact your administrator."
                : "No customers have been created in the system yet."
            }
            icon={isRegularUser ? "add" : "customer"}
            actionLabel={isRegularUser ? "Create Customer" : undefined}
            onAction={
              isRegularUser ? () => setCreateDialogOpen(true) : undefined
            }
          />
        )}

        {/* Empty State - No search results */}
        {!loading &&
          accessibleCustomers?.length > 0 &&
          filteredCustomers.length === 0 &&
          searchQuery && (
            <EmptyState
              title="No customers found"
              description={`No customers match "${searchQuery}". Try a different search term.`}
              icon="search"
            />
          )}

        {/* Customer Grid */}
        {!loading && filteredCustomers.length > 0 && (
          <div
            className={cn(
              "grid gap-6",
              "grid-cols-1",
              "md:grid-cols-2",
              "lg:grid-cols-3",
              "animate-fade-in-up"
            )}
          >
            {filteredCustomers.map((customer) => (
              <CustomerCard
                key={customer.id}
                customer={customer}
                onClick={handleSelectCustomer}
                selected={selectedCustomer?.id === customer.id}
              />
            ))}
          </div>
        )}

        {/* Footer Info */}
        {!loading && filteredCustomers.length > 0 && (
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Showing {filteredCustomers.length} of{" "}
              {accessibleCustomers?.length || 0} customers
            </p>
            {user?.role === "admin" && (
              <p className="text-xs text-gray-400">
                Admin: You have access to all customers in the system
              </p>
            )}
            {user?.role === "consultant" && (
              <p className="text-xs text-gray-400">
                Consultant: Showing your assigned customers only
              </p>
            )}
          </div>
        )}
      </div>

      {/* Create Customer Dialog */}
      <CustomerCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCustomerCreated}
        isRegularUser={isRegularUser}
      />
    </PageContainer>
  );
}
